import type { NextPage } from "next";
import Head from "next/head";
import {
  ManageToken,
  MANAGE_TOKEN_FAQ_ITEMS,
  MANAGE_TOKEN_HOWTOS,
} from "../components/ManageToken";
import {
  SITE_OG_IMAGE,
  SITE_URL,
  SOFTWARE_ID,
  breadcrumb,
  graph,
  howTo,
  ld,
  orgRef,
  softwareApplication,
  webPage,
} from "../lib/schema";

const siteUrl = `${SITE_URL}/manage-token`;
const siteTitle = "Solana Token Manager: Revoke Authority & Mint Tokens";
const siteDescription = "Manage your Solana tokens for free: revoke mint authority, revoke freeze authority, make tokens immutable, and mint additional tokens.";
const siteImage = SITE_OG_IMAGE;

const trail = [
  { name: "SoulMinter", url: SITE_URL },
  { name: "Manage Token", url: siteUrl },
];

const managerApp = {
  "@type": "WebApplication",
  "@id": `${siteUrl}#app`,
  name: "SoulMinter Token Manager",
  url: siteUrl,
  description: siteDescription,
  image: siteImage,
  applicationCategory: "FinanceApplication",
  applicationSubCategory: "Solana token administration",
  operatingSystem: "Web browser",
  browserRequirements:
    "Requires JavaScript. Reading a mint is signature-free; revoking or minting requires a Solana wallet implementing the Wallet Standard.",
  isAccessibleForFree: true,
  isPartOf: { "@id": SOFTWARE_ID },
  provider: orgRef,
  creator: orgRef,
  featureList: [
    "Read any SPL or Token-2022 mint's current authorities without connecting a wallet",
    "Revoke mint, freeze, and update authorities independently",
    "Mint additional supply to one or many addresses",
    "Pause and resume a pausable mint",
    "Manage whitelist (frozen-by-default) accounts",
  ],
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    description:
      "Free to use. You pay only the Solana network transaction fee for any action you sign.",
    seller: orgRef,
  },
  potentialAction: {
    "@type": "UseAction",
    name: "Manage Solana Token",
    target: {
      "@type": "EntryPoint",
      urlTemplate: siteUrl,
      actionPlatform: "https://schema.org/DesktopWebPlatform",
    },
  },
};

/**
 * Both flows are built from MANAGE_TOKEN_HOWTOS, the array the page renders
 * as visible step cards under the authorities primer.
 */
const howTos = MANAGE_TOKEN_HOWTOS.map((flow) =>
  howTo({
    id: `${siteUrl}#howto-${flow.id}`,
    pageUrl: siteUrl,
    name: flow.name,
    description: flow.description,
    steps: flow.steps,
  })
);

/**
 * The FAQ questions come from MANAGE_TOKEN_FAQ_ITEMS, the array the page
 * renders. The previous markup listed five questions of its own, two of
 * which gave different answers than the page did.
 */
const manageGraph = graph([
  softwareApplication,
  managerApp,
  webPage({
    url: siteUrl,
    name: siteTitle,
    description: siteDescription,
    image: siteImage,
    trail,
    faqs: MANAGE_TOKEN_FAQ_ITEMS,
  }),
  breadcrumb(siteUrl, trail),
  ...howTos,
]);

const ManageTokenPage: NextPage = () => {
  return (
    <div>
      <Head>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <meta name="keywords" content="revoke mint authority Solana, revoke freeze authority Solana, make Solana token immutable, renounce ownership Solana token, how to stop minting more tokens Solana, how to disable freeze on my token, lock token supply Solana, Solana token manager tool, SPL token update authority, mint more tokens Solana, increase token supply Solana, Solana mint authority tool, mint to address Solana" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en" />
        <meta name="author" content="SoulMinter" />
        <meta name="revisit-after" content="7 days" />
        <meta name="theme-color" content="#000000" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:site_name" content="SoulMinter" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content={siteImage} />
        <meta name="twitter:creator" content="@soulminter" />
        <meta name="twitter:site" content="@soulminter" />

        <link rel="canonical" href={siteUrl} />

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: ld(manageGraph) }}
        />
      </Head>
      <ManageToken />
    </div>
  );
};

export default ManageTokenPage;
